import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { CentreService } from 'src/app/services/centre.service';
import { FormControl } from '@angular/forms';
import { forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { map, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import * as XLSX from 'xlsx';
import {endOfDay} from '../../../../utils/date-range-format.service';

/**
 * Row model used in the counselling sessions count table.
 * @property centreCode - Unique code of the centre
 * @property centreName - Human-friendly name of the centre
 * @property noOfFamilies - Unique number of families (derived from childCode)
 * @property individualSessions - Count of individual (regular) counselling sessions
 * @property followupSessions - Count of follow-up counselling sessions
 * @property groupSessions - Count of group sessions
 * @property assessments - Count of assessments for the centre
 * @property total - Sum of all numeric counts for quick display/export
 */
interface CentreReportRow {
  centreCode: string;
  centreName: string;
  noOfFamilies: number;
  individualSessions: number;
  followupSessions: number;
  groupSessions: number;
  assessments: number;
  total?: number;
}

/**
 * Component responsible for generating a counselling sessions count report.
 * The component fetches data from Firestore (production collections), aggregates
 * counts per centre and displays the results in a Material table with
 * filtering, sorting and pagination. It also supports export to Excel.
 */
@Component({
  selector: 'app-counselling-sessions-count',
  templateUrl: './counselling-sessions-count.component.html',
  styleUrls: ['./counselling-sessions-count.component.scss']
})
export class CounsellingSessionsCountComponent implements OnInit, AfterViewInit, OnDestroy {
  // View children for client-side pagination & sorting
  @ViewChild(MatPaginator) paginator: MatPaginator | null = null;
  @ViewChild(MatSort) sort: MatSort | null = null;

  /** Observable that emits centre options used to populate the centre dropdown. */
  centres$: Observable<any[]> | undefined;

  /** Selected centre control. Use 'All' to select every centre. */
  centreControl = new FormControl('All');

  /** Start date form control (bound to the Material date-range start). */
  startDateControl = new FormControl('');

  /** End date form control (bound to the Material date-range end). */
  endDateControl = new FormControl('');

  /** Internal array of computed report rows (used to populate dataSource). */
  reportRows: CentreReportRow[] = [];

  /** Loading indicator used during async fetches. */
  loading = false;

  /** MatTableDataSource used by the Material table for filtering/sorting/pagination. */
  dataSource = new MatTableDataSource<CentreReportRow>([]);

  /** Columns displayed by the Material table (ordered). */
  displayedColumns: string[] = [ 'centreName', 'noOfFamilies', 'individualSessions', 'followupSessions', 'groupSessions', 'assessments', 'total'];

  /** Environment production-mode collection map. */
  env = environment.productionMode;

  // debounced search subject and subscription
  private searchSubject = new Subject<string>();
  private searchSub: Subscription | null = null;

  /** Emits (and completes) on component destruction to unsubscribe long-lived subscriptions. */
  private _destroyed$ = new Subject<void>();

  /**
   * @param afs AngularFirestore instance for Firestore queries
   * @param centreService Service to fetch centre metadata/options
   */
  constructor(private readonly afs: AngularFirestore,
              private readonly centreService: CentreService) { }

  /**
   * Initialize component: load centres and set up filter predicate + debounced search.
   */
  ngOnInit(): void {
    // Load centres for dropdown
    this.centres$ = (this.centreService.getAllCentresOptions() as Observable<any[]>).pipe(
      map(centres => [...centres].sort((a, b) => (a.centreName ?? '').trim().localeCompare((b.centreName ?? '').trim())))
    );

    // set default filter predicate to search by centre name or code
    this.dataSource.filterPredicate = (data: CentreReportRow, filter: string) => {
      const f = filter.trim().toLowerCase();
      return (data.centreName + ' ' + data.centreCode).toLowerCase().includes(f);
    };

    // subscribe to debounced search input (300ms)
    this.searchSub = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {
      this.dataSource.filter = value.trim().toLowerCase();
    });
  }

  /**
   * Wire paginator and sorter to the MatTableDataSource after the view is initialized.
   */
  ngAfterViewInit(): void {
    if (this.paginator) this.dataSource.paginator = this.paginator;
    if (this.sort) this.dataSource.sort = this.sort;
  }

  /**
   * Clean up subscriptions when the component is destroyed.
   */
  ngOnDestroy(): void {
    if (this.searchSub) this.searchSub.unsubscribe();
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /**
   * Convert an input into a Date object if possible.
   * Accepts Date instances or date-like strings and returns undefined for invalid inputs.
   * @param value Date|string|unknown - value to coerce to Date
   * @returns Date | undefined
   */
  private toDate(value: any): Date | undefined {
    if (!value) return undefined;
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return undefined;
      return d;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * Build and populate the report rows.
   * Fetches individual counselling sessions, group sessions and assessments in parallel
   * from the production collections (date-filtered if start/end are provided).
   * Aggregates per-centre counts and populates the table datasource.
   * This method sets `loading` true while fetching and resets it when complete.
   */
  generateReport() {
    this.loading = true;
    this.reportRows = [];

    const startDate = this.toDate(this.startDateControl.value);
    const endDate = this.toDate(this.endDateControl.value);

    // Build query constraints (use date fields used in the project)
    const counsellingPath = `${this.env.counseling}/allSessions/sessions`;
    const groupPath = `${this.env.counseling}/allSessions/groupSessions`;
    const assessmentPath = `${this.env.assessment}/allAssessments/assessments`;

    // Fetch all centres first (for rows). We'll include an "All" option in dropdown; when a specific centre is selected we'll filter afterwards
    (this.centres$ as Observable<any[]>).pipe(takeUntil(this._destroyed$)).subscribe(centresList => {
      const centreNames = centresList.map(c => ({ centreCode: c.centreCode, centreName: c.centreName }));

      // Prepare queries
      const counsellingCol = this.afs.collection<any>(counsellingPath, ref => {
        let r: any = ref;
        if (startDate) r = r.where('date', '>=', startDate);
        if (endDate) r = r.where('date', '<=', endOfDay(endDate));
        return r;
      }).get().pipe(map(qs => qs.docs.map(d => d.data())));

      const groupCol = this.afs.collection<any>(groupPath, ref => {
        let r: any = ref;
        if (startDate) r = r.where('date', '>=', startDate);
        if (endDate) r = r.where('date', '<=', endOfDay(endDate));
        return r;
      }).get().pipe(map(qs => qs.docs.map(d => d.data())));

      const assessmentCol = this.afs.collection<any>(assessmentPath, ref => {
        let r: any = ref;
        if (startDate) r = r.where('dateOfAssessment', '>=', startDate);
        if (endDate) r = r.where('dateOfAssessment', '<=', endOfDay(endDate));
        return r;
      }).get().pipe(map(qs => qs.docs.map(d => d.data())));

      forkJoin([counsellingCol, groupCol, assessmentCol]).pipe(takeUntil(this._destroyed$)).subscribe({
        next: ([counsellingSessions, groupSessions, assessments]) => {
          // If a specific centre selected (not 'All') filter the centre list to only that centre
          let selected = this.centreControl.value || 'All';

          const centresToProcess = selected === 'All' ? centreNames : centreNames.filter(c => c.centreCode === selected || c.centreName === selected);

          centresToProcess.forEach(c => {
            // Counselling sessions filtered by centre
            const centreCounselling = counsellingSessions.filter((s: any) => s.centreCode === c.centreCode || s.centreName === c.centreName);

            // noOfFamilies - inferred as unique childCode count in counselling sessions (assumption)
            const uniqueFamilies = Array.from(new Set(centreCounselling.map((s: any) => s.childCode).filter(Boolean)));

            const individualSessions = centreCounselling.filter((s: any) => (s.type || '').toString() === 'Regular Counselling').length;
            const followupSessions = centreCounselling.filter((s: any) => (s.type || '').toString() === 'Follow-up Counselling').length;

            // group sessions
            const centreGroup = groupSessions.filter((g: any) => g.centreCode === c.centreCode || g.centres?.some((x: any) => x?.centreCode === c.centreCode || x?.centreName === c.centreName));

            const assessmentsForCentre = assessments.filter((a: any) => a.centreCode === c.centreCode || a.centreName === c.centreName);

            this.reportRows.push({
              centreCode: c.centreCode,
              centreName: c.centreName,
              noOfFamilies: uniqueFamilies.length,
              individualSessions: individualSessions,
              followupSessions: followupSessions,
              groupSessions: centreGroup.length,
              assessments: assessmentsForCentre.length
            });
          });

          // Sort rows by centreName
          this.reportRows.sort((a, b) => a.centreName.localeCompare(b.centreName));
          // compute totals for each row (sum of numeric cols)
          this.reportRows = this.reportRows.map(r => ({ ...r, total: (Number(r.noOfFamilies) || 0) + (Number(r.individualSessions) || 0) + (Number(r.followupSessions) || 0) + (Number(r.groupSessions) || 0) + (Number(r.assessments) || 0) }));
           // populate the MatTableDataSource
           this.dataSource.data = this.reportRows.slice();
           this.loading = false;
         },
        error: (err) => {
          console.error('Error generating report', err);
          this.loading = false;
        }
      });

    }, err => {
      console.error('Failed to load centres for report', err);
      this.loading = false;
    });
  }
  addLog(){

  }
  /**
   * Handle the search input change from the template. The search value is
   * emitted to an internal debounced Subject so the table filtering is
   * performed after a short delay.
   * @param event - Keyboard event from the search input
   */
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    // emit to debounced subject
    this.searchSubject.next(filterValue);
  }

  /**
   * Export the currently visible table rows to an Excel (.xlsx) file.
   * The exported file contains all visible columns and a filename with the
   * selected date range (if present).
   */
  exportexcel() {
    try {
      const data = this.dataSource.data.map(r => ({
        'Centre Code': r.centreCode,
        'Centre Name': r.centreName,
        'No. of Families': r.noOfFamilies,
        'Individual Sessions': r.individualSessions,
        'Follow-up Sessions': r.followupSessions,
        'Group Sessions': r.groupSessions,
        'Assessments': r.assessments,
        'Total': r.total || 0
      }));

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Counselling Count');

      const start = this.startDateControl.value ? this.formatDate(this.startDateControl.value) : 'NA';
      const end = this.endDateControl.value ? this.formatDate(this.endDateControl.value) : 'NA';
      const filename = `Counselling_Count_Report (${start} - ${end}).xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error('Export failed', err);
    }
  }

  /**
   * Format a Date (or date-like value) as YYYY-MM-DD for use in the export filename.
   * Returns 'NA' for invalid inputs.
   * @param d - Date or date-string
   * @returns formatted string like '2025-12-02' or 'NA'
   */
  private formatDate(d: any) {
    if (!d) return 'NA';
    const date = new Date(d);
    if (isNaN(date.getTime())) return 'NA';
    return `${date.getFullYear()}-${(date.getMonth()+1).toString().padStart(2,'0')}-${date.getDate().toString().padStart(2,'0')}`;
  }
}
